/**
 * Validation Utilities
 *
 * Centralized validation logic for forms across the application.
 * Provides consistent validation rules and error messages.
 */

// Email regex pattern - follows standard email format
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates an email address
 * @param email - The email address to validate
 * @returns Object with isValid flag and error message
 */
export const validateEmail = (email: string): { isValid: boolean; error: string } => {
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }
  if (!EMAIL_REGEX.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  return { isValid: true, error: '' };
};

/**
 * Validates a required field
 * @param value - The value to validate
 * @param fieldName - The display name of the field for error messages
 * @returns Object with isValid flag and error message
 */
export const validateRequired = (value: string, fieldName: string): { isValid: boolean; error: string } => {
  if (!value?.trim()) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  return { isValid: true, error: '' };
};

/**
 * Validates password strength
 * @param password - The password to validate
 * @param minLength - Minimum password length (default: 6)
 * @returns Object with isValid flag and error message
 */
export const validatePassword = (password: string, minLength: number = 6): { isValid: boolean; error: string } => {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }
  if (password.length < minLength) {
    return { isValid: false, error: `Password must be at least ${minLength} characters` };
  }
  return { isValid: true, error: '' };
};

/**
 * Validates that two passwords match
 * @param password - The original password
 * @param confirmPassword - The confirmation password
 * @returns Object with isValid flag and error message
 */
export const validatePasswordMatch = (password: string, confirmPassword: string): { isValid: boolean; error: string } => {
  if (!confirmPassword) {
    return { isValid: false, error: 'Please confirm your password' };
  }
  if (password !== confirmPassword) {
    return { isValid: false, error: 'Passwords do not match' };
  }
  return { isValid: true, error: '' };
};
